import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Stack,
  Title,
  Text,
  Card,
  Skeleton,
  Badge,
  Group,
  Divider,
  AspectRatio,
  Box,
  Alert,
  Button,
  Anchor,
  List,
  ThemeIcon,
  Collapse,
} from '@mantine/core';
import {
  IconFileText,
  IconPlayerPlay,
  IconHeadphones,
  IconPhoto,
  IconLink,
  IconListCheck,
  IconAlertTriangle,
  IconInfoCircle,
  IconTextCaption,
} from '@tabler/icons-react';
import { usePublicKnowledgeBaseService, useKBStepTypeService } from '@actschurch/ccms-sdk';
import type { KBStepModelView, KBStepType, ResourceMediaView } from '@actschurch/ccms-sdk';
import { useOptionalAuth } from '@/hooks/useOptionalAuth';
import { useArticleStepsMedia, usePublicResourceSettings, useRemoteText } from '@/hooks/useSessionMedia';
import { HlsVideo } from '@/components/player/HlsVideo';
import { CaptionsTrack } from '@/components/player/CaptionsTrack';
import { TranscriptPanel } from '@/components/player/TranscriptPanel';
import { transcriptSourceFor } from '@/components/player/transcriptSource';
import { createPlaybackController, useBindMediaElement } from '@/components/player/playbackController';
import { RichText } from '@/components/common/RichText';
import { VisibilityBadge } from '@/components/common/VisibilityBadge';

/**
 * KB article reader. Each step is rendered by type — text / video / audio /
 * pdf / image / link / checklist / warning / callout.
 *
 * Article + steps + category come from the public KB surface, which enforces
 * status + visibility + audience server-side — so anonymous visitors see the
 * Public-visibility content and signed-in users additionally see whatever
 * matches their audience.
 */
export default function KBArticle() {
  const params = useParams();
  const { isAuthenticated } = useOptionalAuth();
  const publicKb = usePublicKnowledgeBaseService();
  const stepTypeService = useKBStepTypeService();

  const slug = params.slug;

  // Single API call: the server walks KB → Category → Article and applies the
  // visibility rule engine before returning. A 404 here is the legitimate
  // "not visible to you" response (or a real not-found) — both render the
  // same below.
  const { data: article, isLoading } = useQuery({
    queryKey: ['public-kb-article', slug],
    queryFn: () => (slug ? publicKb.getArticleBySlug(slug) : Promise.resolve(null)),
    enabled: !!slug,
  });

  const { data: steps = [] } = useQuery({
    queryKey: ['public-kb-steps', article?.kbArticleID],
    queryFn: () =>
      article ? publicKb.listArticleSteps(article.kbArticleID) : Promise.resolve([]),
    enabled: !!article?.kbArticleID,
  });

  // Step types are pure reference data (icon + label per type) and live on
  // the authenticated admin service. For anonymous visitors we just render
  // the default text/file icon — readability isn't impacted.
  const { data: stepTypes = [] } = useQuery({
    queryKey: ['kb-step-types'],
    queryFn: () => stepTypeService.getAll(),
    enabled: isAuthenticated,
  });

  const { data: category } = useQuery({
    queryKey: ['public-kb-category', article?.kbCategoryID],
    queryFn: () =>
      article ? publicKb.getCategory(article.kbCategoryID) : Promise.resolve(null),
    enabled: !!article?.kbCategoryID,
  });

  const typesById = useMemo(
    () => new Map(stepTypes.map((t) => [t.stepTypeID, t])),
    [stepTypes],
  );

  // Streaming media (HLS, captions, transcripts) for video / audio steps.
  // Only fetched when the article has such steps; any failure leaves the
  // steps playing their original files.
  const hasMediaSteps = useMemo(
    () =>
      steps.some((s) => {
        const k = stepKind(s, typesById.get(s.stepTypeID));
        return !!s.mediaURL && (k.includes('video') || k.includes('audio'));
      }),
    [steps, typesById],
  );
  const { data: stepsMedia } = useArticleStepsMedia(article?.kbArticleID, hasMediaSteps);
  const { data: publicSettings } = usePublicResourceSettings();
  const transcriptsOff = publicSettings?.enableTranscripts === false;

  if (isLoading) {
    return (
      <Stack gap="md">
        <Skeleton height={24} w={240} />
        <Skeleton height={40} w={480} />
        <Skeleton height={200} />
      </Stack>
    );
  }

  if (!article) {
    return (
      <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light">
        Article not found.
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      <Group gap="xs">
        <Anchor component={Link} to="/kb" c="dimmed" size="sm">
          ← Back to Knowledge Base
        </Anchor>
      </Group>

      <Stack gap="xs">
        <Group gap="xs">
          {category && (
            <Badge color={category.color ?? 'brand'} variant="light">
              {category.name}
            </Badge>
          )}
          {/* Visibility chip — Unlisted/Restricted; Public renders nothing. */}
          <VisibilityBadge
            visibilityName={article.visibilityName}
            visibilityColor={article.visibilityColor}
          />
        </Group>
        <Title order={1}>{article.title}</Title>
        <RichText html={article.summary} />
        {article.readingTimeMinutes != null && (
          <Text size="xs" c="dimmed">
            ~ {article.readingTimeMinutes} min read
          </Text>
        )}
      </Stack>

      <Divider />

      <Stack gap="lg">
        {steps.length === 0 ? (
          <Text c="dimmed">This article has no steps yet.</Text>
        ) : (
          steps.map((step, i) => (
            <KBStepBlock
              key={step.kbStepID}
              step={step}
              index={i}
              type={typesById.get(step.stepTypeID)}
              media={stepsMedia?.get(step.kbStepID) ?? null}
              transcriptsOff={transcriptsOff}
            />
          ))
        )}
      </Stack>
    </Stack>
  );
}

/** Fallback step type names by id, for when neither the lookup nor the payload names the type. */
const STEP_TYPE_FALLBACK: Record<number, string> = { 2: 'Video', 3: 'Audio', 4: 'PDF' };

/**
 * Display name of a step's type. The lookup list only loads for signed-in
 * viewers, so fall back to the name on the public payload, then to the id.
 */
function stepTypeName(step: KBStepModelView, type?: KBStepType): string | null {
  return type?.name ?? step.stepTypeName ?? STEP_TYPE_FALLBACK[step.stepTypeID] ?? null;
}

function stepKind(step: KBStepModelView, type?: KBStepType): string {
  return (stepTypeName(step, type) ?? '').toLowerCase();
}

function KBStepBlock({
  step,
  index,
  type,
  media,
  transcriptsOff,
}: {
  step: KBStepModelView;
  index: number;
  type?: KBStepType;
  media?: ResourceMediaView | null;
  transcriptsOff?: boolean;
}) {
  const typeName = stepTypeName(step, type);
  const kind = (typeName ?? '').toLowerCase();
  const stepNumber = index + 1;
  // Two anchors: by-position (#step-3) and by-id (#kb-step-42). Search citations
  // can target either; the by-position form is what the cite-builder defaults to.
  const positionAnchor = `step-${stepNumber}`;
  const idAnchor = `kb-step-${step.kbStepID}`;
  const isHighlighted = useIsHashTarget(positionAnchor, idAnchor);

  return (
    <Card
      id={positionAnchor}
      withBorder
      padding="lg"
      radius="lg"
      style={{
        scrollMarginTop: 80,
        borderColor: isHighlighted ? 'var(--mantine-color-brand-6)' : undefined,
        boxShadow: isHighlighted ? '0 0 0 3px var(--mantine-color-brand-2)' : undefined,
        transition: 'box-shadow 200ms ease, border-color 200ms ease',
      }}
    >
      <span id={idAnchor} style={{ position: 'absolute', marginTop: -80 }} aria-hidden />
      <Group gap="sm" mb="sm" align="center">
        <ThemeIcon
          variant="light"
          color={type?.color ?? 'brand'}
          size={32}
          radius="xl"
          aria-label={typeName ?? 'Step'}
        >
          <StepIcon kind={kind} />
        </ThemeIcon>
        <Stack gap={0}>
          <Text size="xs" c="dimmed">
            Step {stepNumber} · {typeName ?? 'Text'}
          </Text>
          {step.title && <Text fw={600}>{step.title}</Text>}
        </Stack>
      </Group>

      <StepBody step={step} kind={kind} media={media} transcriptsOff={transcriptsOff} />
    </Card>
  );
}

/**
 * Track whether the current URL hash matches one of the supplied anchor ids,
 * so the step can highlight itself when an AI search citation deep-links to
 * it. Live-listens to <c>hashchange</c> so subsequent in-page navigations
 * (e.g. clicking a TOC entry) re-trigger the highlight.
 */
function useIsHashTarget(...anchors: string[]): boolean {
  const [hash, setHash] = useState<string>(typeof window !== 'undefined' ? window.location.hash : '');
  useEffect(() => {
    const handler = () => setHash(window.location.hash);
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);
  return anchors.some((a) => hash === `#${a}`);
}

function StepIcon({ kind }: { kind: string }) {
  if (kind.includes('video')) return <IconPlayerPlay size={14} />;
  if (kind.includes('audio')) return <IconHeadphones size={14} />;
  if (kind.includes('pdf')) return <IconFileText size={14} />;
  if (kind.includes('image')) return <IconPhoto size={14} />;
  if (kind.includes('link')) return <IconLink size={14} />;
  if (kind.includes('check')) return <IconListCheck size={14} />;
  if (kind.includes('warning')) return <IconAlertTriangle size={14} />;
  if (kind.includes('callout')) return <IconInfoCircle size={14} />;
  return <IconFileText size={14} />;
}

function StepBody({
  step,
  kind,
  media,
  transcriptsOff,
}: {
  step: KBStepModelView;
  kind: string;
  media?: ResourceMediaView | null;
  transcriptsOff?: boolean;
}) {
  if (kind.includes('video') && step.mediaURL) {
    return <KBMediaStep step={step} mediaURL={step.mediaURL} kind="video" media={media} transcriptsOff={transcriptsOff} />;
  }
  if (kind.includes('audio') && step.mediaURL) {
    return <KBMediaStep step={step} mediaURL={step.mediaURL} kind="audio" media={media} transcriptsOff={transcriptsOff} />;
  }
  if (kind.includes('pdf') && step.mediaURL) {
    return (
      <AspectRatio ratio={4 / 5}>
        <iframe src={step.mediaURL} title={step.title ?? 'PDF'} style={{ border: 0, width: '100%', height: '100%' }} />
      </AspectRatio>
    );
  }
  if (kind.includes('image') && step.mediaURL) {
    return <img src={step.mediaURL} alt={step.title ?? ''} style={{ maxWidth: '100%', borderRadius: 8 }} />;
  }
  if (kind.includes('link') && step.mediaURL) {
    return (
      <Button component="a" href={step.mediaURL} target="_blank" rel="noopener noreferrer" variant="light">
        Open link
      </Button>
    );
  }
  if (kind.includes('check') && step.content) {
    // Checklist content is stored as JSON ({ items: [...] }) by the
    // ChecklistRepeater in ccms_v2. Older entries are still raw text with
    // one item per line, so we fall back to that if JSON parsing fails.
    const items = parseChecklistContent(step.content);
    return (
      <List spacing="xs" icon={<ThemeIcon size={20} radius="xl" color="teal"><IconListCheck size={12} /></ThemeIcon>}>
        {items.map((it, i) => (
          <List.Item key={i}>{it}</List.Item>
        ))}
      </List>
    );
  }
  if (kind.includes('warning')) {
    return (
      <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light">
        <RichText html={step.content} />
      </Alert>
    );
  }
  if (kind.includes('callout')) {
    return (
      <Alert icon={<IconInfoCircle size={16} />} color="yellow" variant="light">
        <RichText html={step.content} />
      </Alert>
    );
  }
  // Default text step — content is HTML written in the admin editor.
  return (
    <Box>
      <RichText html={step.content} />
    </Box>
  );
}

/**
 * Video or audio step. Plays the original file exactly as before unless the
 * step has streaming media: then video streams over HLS (falling back to the
 * file on any error) with captions, and a compact transcript can be opened
 * under the player.
 */
function KBMediaStep({
  step,
  mediaURL,
  kind,
  media,
  transcriptsOff,
}: {
  step: KBStepModelView;
  mediaURL: string;
  kind: 'video' | 'audio';
  media?: ResourceMediaView | null;
  transcriptsOff?: boolean;
}) {
  const [controller] = useState(createPlaybackController);
  const bind = useBindMediaElement(controller);
  const captions = useRemoteText(kind === 'video' && !transcriptsOff ? media?.captionsVttUrl : null);
  const transcript = transcriptSourceFor(media, !!transcriptsOff);
  const [showTranscript, setShowTranscript] = useState(false);
  const fileNameBase = (step.title ?? `step-${step.kbStepID}`).replace(/[^a-z0-9]+/gi, '-').toLowerCase();

  return (
    <Stack gap="xs">
      {kind === 'video' ? (
        <AspectRatio ratio={16 / 9}>
          <HlsVideo
            ref={bind}
            hlsUrl={media?.hlsUrl ?? null}
            fallbackSrc={mediaURL}
            controls
            poster={step.thumbnail ?? media?.posterUrl ?? undefined}
            style={{ width: '100%', height: '100%', background: 'black' }}
          >
            {captions.data ? <CaptionsTrack vttText={captions.data} language={media?.transcriptLanguage} /> : null}
          </HlsVideo>
        </AspectRatio>
      ) : (
        <audio ref={bind} src={mediaURL} controls style={{ width: '100%' }} />
      )}
      {transcript && (
        <>
          <Group gap="xs">
            <Button
              size="compact-xs"
              variant="subtle"
              leftSection={<IconTextCaption size={12} />}
              onClick={() => setShowTranscript((v) => !v)}
              aria-expanded={showTranscript}
            >
              {showTranscript ? 'Hide transcript' : 'Show transcript'}
            </Button>
          </Group>
          <Collapse in={showTranscript}>
            {showTranscript && (
              <Card withBorder radius="md" padding={0}>
                <TranscriptPanel compact {...transcript} controller={controller} fileNameBase={fileNameBase} />
              </Card>
            )}
          </Collapse>
        </>
      )}
    </Stack>
  );
}

/**
 * Parse a checklist step's content into a list of items. Handles two
 * formats:
 *  - New (current): JSON object `{ items: [...] }` written by the
 *    ChecklistRepeater in ccms_v2.
 *  - Legacy: plain text, one item per line.
 * Returns an empty list when nothing usable is present.
 */
function parseChecklistContent(raw: string): string[] {
  const trimmed = raw.trim();
  if (trimmed.startsWith('{')) {
    try {
      const obj = JSON.parse(trimmed);
      if (Array.isArray(obj?.items)) {
        return obj.items
          .map((s: unknown) => String(s ?? '').trim())
          .filter((s: string) => s.length > 0);
      }
    } catch {
      /* fall through to legacy parsing */
    }
  }
  return trimmed
    .split('\n')
    .map((l) => l.replace(/^\s*[-*]\s*/, '').trim())
    .filter(Boolean);
}
